import {GuestConsentProvider} from '@backstage-components/guest-consent-provider';
import {AttendeeProvider} from '@backstage-components/attendee-container';
import type {AppPage} from '@backstage-components/base';
import {
  DocumentVisibilityProvider,
  ShowInstructionsProvider,
  useDocumentVisibility,
} from '@backstage-components/base';
import {isLocalhost, isPreviewDomain} from '@backstage/utils/domain-helpers';
import {FC, PropsWithChildren} from 'react';
import {HelmetProvider} from 'react-helmet-async';
import {client} from './apollo';
import {config} from './config';
import {
  useAnalytics,
  useFetchInstructions,
  type UseFetchInstructionsOptions,
} from './hooks';
import {JsonValue} from 'type-fest';

const analyticsBlockList: string[] = ['xboxfanfestchallenge.com'];

type ShowInstructionsProviderProps = Parameters<
  typeof ShowInstructionsProvider
>[0];

/**
 * Wrapper for the `ShowInstructionsProvider` that is necessary to utilize the
 * `useDocumentVisibility` hook without needing to wrap `ShowInstructionsProvider`
 * everywhere.
 */
const InstructionsProviderWrapper: FC<
  PropsWithChildren<
    Omit<ShowInstructionsProviderProps, 'isInstructionFetchingPaused'>
  >
> = (props) => {
  const {domainName} = props;
  const isInstructionFetchingPaused = 'hidden' === useDocumentVisibility();
  const instructionFetcher = useFetchInstructions({
    appKey: config.pusherKey,
    cluster: config.pusherCluster,
    showControllerType: props.showControllerType,
    showId: props.showId,
    isInstructionFetchingPaused,
  });
  const analytics = useAnalytics(
    typeof domainName === 'string' &&
      analyticsBlockList.some((host) => domainName.endsWith(host))
      ? 'disabled'
      : 'default'
  );
  const location = typeof window === 'undefined' ? undefined : window.location;
  const debugEnabled =
    isPreviewDomain(config, location) || isLocalhost(config, location);

  return (
    <ShowInstructionsProvider
      allowedEmbedOrigins={['*']}
      analyticsEndpoint={analytics?.endpoint}
      analyticsToken={analytics?.token}
      appPages={props.appPages}
      domainName={props.domainName ?? 'unknown'}
      rootElement={props.rootElement}
      fetchInstructions={instructionFetcher}
      showId={props.showId}
      siteVariableLookup={props.siteVariableLookup}
      debugEnabled={debugEnabled}
      isInstructionFetchingPaused={isInstructionFetchingPaused}
      showControllerType={props.showControllerType}
    >
      {props.children}
    </ShowInstructionsProvider>
  );
};

export const AppContext: FC<PropsWithChildren<AppContextProps>> = ({
  appPages,
  children,
  domainName,
  rootElement,
  showControllerType = 'POLL',
  showId = FALLBACK_SHOW_ID,
  siteVariableLookup,
}) => {
  return (
    <DocumentVisibilityProvider>
      <InstructionsProviderWrapper
        appPages={appPages}
        domainName={domainName}
        rootElement={rootElement}
        showId={showId}
        siteVariableLookup={siteVariableLookup}
        showControllerType={showControllerType}
      >
        <AttendeeProvider client={client} showId={showId}>
          <GuestConsentProvider domainName={domainName}>
            <HelmetProvider>{children}</HelmetProvider>
          </GuestConsentProvider>
        </AttendeeProvider>
      </InstructionsProviderWrapper>
    </DocumentVisibilityProvider>
  );
};

const FALLBACK_SHOW_ID = '00000000-0000-0000-0000-000000000000';

interface AppContextProps
  extends Pick<UseFetchInstructionsOptions, 'showControllerType' | 'showId'> {
  appPages: AppPage[];
  /** Domain on which the `showId` was viewed */
  domainName?: string;
  /** Element at the root of the application */
  rootElement?: HTMLElement;
  siteVariableLookup: Record<string, JsonValue>;
}
